import { useState } from "react";
import { Link } from "react-router-dom";
import NetworkGraph from "../../components/NetworkGraph";
import Reveal from "../../components/Reveal";
import { IconChat } from "../../components/Icons";

const ACCENT = "#00C2A0";

const FAQS = [
  {
    question: "Quantos atendentes podem usar ao mesmo tempo?",
    answer:
      "O número de atendentes simultâneos depende do plano contratado. Entre em contato para receber uma proposta adequada ao tamanho da sua equipe.",
  },
  {
    question: "O cliente percebe que estou usando uma plataforma?",
    answer:
      "Não. Para o cliente é uma conversa normal pelo WhatsApp. Apenas internamente a equipe usa a plataforma para organizar e distribuir os atendimentos.",
  },
  {
    question: "É possível usar chatbot?",
    answer:
      "Sim. O sepolCHAT suporta automações e chatbots para respostas automáticas, triagem inicial e atendimento fora do horário comercial.",
  },
];

const FEATURES = [
  {
    title: "WhatsApp Multiatendimento",
    desc: "Vários atendentes no mesmo número de WhatsApp sem conflito",
  },
  {
    title: "Omnichannel",
    desc: "Centralize WhatsApp, e-mail e outros canais em uma única plataforma",
  },
  {
    title: "Histórico Completo",
    desc: "Todo o histórico de conversas salvo e acessível por qualquer atendente",
  },
  {
    title: "Filas e Departamentos",
    desc: "Organize atendimentos por departamento, com filas e transferências",
  },
  {
    title: "Relatórios de Atendimento",
    desc: "Métricas de tempo de resposta, volume e desempenho da equipe",
  },
  {
    title: "Gestão pela sepolTECH",
    desc: "Hospedagem, manutenção e suporte incluídos na assinatura",
  },
];

export default function SepolChat() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  return (
    <>
      {/* ═══════════════ ABERTURA ═══════════════ */}
      <section className="page-hero">
        <div className="page-hero__bg"></div>
        <div className="page-hero__grid"></div>
        <NetworkGraph variant="hero" className="page-hero__network" />
        <div className="container">
          <div className="breadcrumb animate-up">
            <Link to="/">Início</Link>
            <span>/</span>
            <Link to="/produtos/produtos">Produtos</Link>
            <span>/</span>
            <span>sepolCHAT</span>
          </div>
          <div className="tag animate-up animate-up--d1" style={{ color: ACCENT, background: "rgba(0,194,160,.12)", borderColor: `${ACCENT}40` }}>
            <IconChat style={{ width: "13px", height: "13px" }} />
            CX Platform · Omnichannel
          </div>
          <h1
            className="animate-up animate-up--d2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(40px,6vw,72px)",
              fontWeight: 700,
              letterSpacing: "-.03em",
              lineHeight: "1.05",
              marginBottom: "24px",
              color: "var(--text-primary)",
            }}
          >
            sepol<span style={{ color: ACCENT }}>CHAT</span>
          </h1>
          <p
            className="animate-up animate-up--d3"
            style={{
              fontSize: "18px",
              color: "var(--text-secondary)",
              maxWidth: "540px",
              lineHeight: "1.7",
              marginBottom: "40px",
            }}
          >
            Plataforma omnichannel de atendimento ao cliente. WhatsApp e
            múltiplos canais em uma única tela para toda a equipe — sem
            caos, com histórico completo.
          </p>
          <div className="hero-actions-col animate-up animate-up--d4">
            <Link to="/contato" className="btn btn-primary">
              Contratar sepolCHAT →
            </Link>
            <Link to="/contato" className="btn btn-outline">
              Falar com especialista
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════ RECURSOS INCLUSOS ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="tag" style={{ color: ACCENT, background: "rgba(0,194,160,.12)", borderColor: `${ACCENT}40` }}>
              Recursos inclusos
            </div>
            <h2 className="section-title" style={{ marginBottom: "36px" }}>
              O que você recebe com <span style={{ color: ACCENT }}>sepolCHAT</span>
            </h2>
            <div className="feature-list">
              {FEATURES.map((feature) => (
                <div className="feature-item" key={feature.title}>
                  <div
                    className="feature-item__icon"
                    style={{ background: "rgba(0,194,160,.12)", color: ACCENT }}
                  >
                    ✓
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>{feature.title}</strong> —{" "}
                    {feature.desc}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="section section--alt">
        <div className="container" style={{ maxWidth: "760px" }}>
          <Reveal className="text-center" style={{ marginBottom: "40px" }}>
            <div className="tag" style={{ color: ACCENT, background: "rgba(0,194,160,.12)", borderColor: `${ACCENT}40` }}>
              FAQ
            </div>
            <h2 className="section-title">
              Dúvidas sobre <span style={{ color: ACCENT }}>sepolCHAT</span>
            </h2>
          </Reveal>

          {FAQS.map((faq, index) => (
            <Reveal
              className={`faq-item${openFaq === index ? " open" : ""}`}
              delay={index * 0.05}
              key={faq.question}
            >
              <button
                className="faq-btn"
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaq === index}
              >
                {faq.question}
                <span className="faq-btn__icon">+</span>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal className="cta-banner">
            <h2 className="cta-banner__title">
              Atendimento organizado, clientes satisfeitos
            </h2>
            <p className="cta-banner__sub">
              Centralize seus canais e eleve o nível do atendimento da sua
              empresa.
            </p>
            <div className="cta-banner__actions">
              <Link to="/contato" className="btn btn-primary">
                Contratar sepolCHAT →
              </Link>
              <Link to="/produtos/produtos" className="btn btn-outline">
                Ver outros produtos
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
